import React, { useState, useEffect } from 'react';
import { Bug, AlertCircle, CheckCircle, Clock, Mail, Eye, Trash2, Filter, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BugReportsManager = () => {
  const [bugs, setBugs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBug, setSelectedBug] = useState(null);
  const [showResolveDialog, setShowResolveDialog] = useState(null);
  const [resolutionMessage, setResolutionMessage] = useState('');
  const [includeSurvey, setIncludeSurvey] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    fetchBugs();
  }, [statusFilter, categoryFilter]);

  const fetchBugs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/bug-reports/admin/list?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBugs(res.data.bugs || []);
      setStats(res.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch bugs:', error);
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (bugId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/bug-reports/admin/${bugId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchBugs();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleResolve = async () => {
    if (!resolutionMessage.trim()) {
      toast.error('Please enter a resolution message');
      return;
    }
    
    setResolving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/bug-reports/admin/${showResolveDialog.id}/resolve`, {
        resolution_message: resolutionMessage,
        include_survey: includeSurvey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bug resolved and reporter notified!');
      setShowResolveDialog(null);
      setResolutionMessage('');
      fetchBugs();
    } catch (error) {
      toast.error('Failed to resolve bug');
    } finally {
      setResolving(false);
    }
  };

  const handleDelete = async (bugId) => {
    if (!window.confirm('Are you sure you want to delete this bug report?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/bug-reports/admin/${bugId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bug report deleted');
      fetchBugs();
      if (selectedBug?.id === bugId) setSelectedBug(null);
    } catch (error) {
      toast.error('Failed to delete bug report');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'closed': return <AlertCircle className="h-4 w-4 text-gray-600" />;
      default: return <Bug className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-red-100 text-red-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      website_issue: 'Website Issue',
      checkout_problem: 'Checkout',
      account_issue: 'Account',
      affiliate_dashboard: 'Affiliate',
      product_page: 'Product Page',
      cart_issue: 'Cart',
      payment_issue: 'Payment',
      mobile_issue: 'Mobile',
      other: 'Other'
    };
    return labels[cat] || cat;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-sm text-gray-500">Total Reports</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-red-500">
          <p className="text-sm text-gray-500">New</p>
          <p className="text-2xl font-bold text-red-600">{stats?.new || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-yellow-500">
          <p className="text-sm text-gray-500">In Progress</p>
          <p className="text-2xl font-bold text-yellow-600">{stats?.in_progress || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
          <p className="text-sm text-gray-500">Resolved</p>
          <p className="text-2xl font-bold text-green-600">{stats?.resolved || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-sm text-gray-500 flex items-center gap-1"><Star className="h-4 w-4" /> Avg Rating</p>
          <p className="text-2xl font-bold text-amber-600">{stats?.avg_satisfaction || '—'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm flex gap-4 items-center">
        <Filter className="h-5 w-5 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
        >
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
        >
          <option value="">All Categories</option>
          <option value="website_issue">Website Issue</option>
          <option value="checkout_problem">Checkout</option>
          <option value="account_issue">Account</option>
          <option value="affiliate_dashboard">Affiliate</option>
          <option value="cart_issue">Cart</option>
          <option value="payment_issue">Payment</option>
          <option value="other">Other</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchBugs}>
          Refresh
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Bug List */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : bugs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Bug className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No bug reports found</p>
            </div>
          ) : (
            <div className="divide-y">
              {bugs.map((bug) => (
                <div 
                  key={bug.id} 
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedBug?.id === bug.id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedBug(bug)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(bug.status)}
                      <div>
                        <p className="font-medium text-gray-900">{bug.title}</p>
                        <p className="text-sm text-gray-500">{bug.reporter_name} • {bug.reporter_email}</p>
                        <div className="flex gap-2 mt-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(bug.status)}`}>
                            {bug.status?.replace('_', ' ')}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                            {getCategoryLabel(bug.category)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">
                      {bug.created_at ? new Date(bug.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bug Details */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {selectedBug ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Bug Details</h3>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">{selectedBug.id?.slice(0, 8).toUpperCase()}</code>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Title</p>
                <p className="font-medium">{selectedBug.title}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="text-sm bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{selectedBug.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Reporter</p>
                  <p className="font-medium text-sm">{selectedBug.reporter_name}</p>
                  <p className="text-xs text-gray-500">{selectedBug.reporter_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-medium text-sm">{getCategoryLabel(selectedBug.category)}</p>
                </div>
              </div>
              
              {selectedBug.page_url && (
                <div>
                  <p className="text-sm text-gray-500">Page URL</p>
                  <a href={selectedBug.page_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                    {selectedBug.page_url}
                  </a>
                </div>
              )}
              
              {selectedBug.screenshot_url && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Screenshot</p>
                  <img 
                    src={`${API_URL}${selectedBug.screenshot_url}`} 
                    alt="Bug screenshot" 
                    className="rounded-lg border max-h-48 object-contain"
                  />
                </div>
              )}
              
              {selectedBug.satisfaction_rating && (
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium flex items-center gap-1">
                    <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                    Rating: {selectedBug.satisfaction_rating}/5
                  </p>
                  {selectedBug.satisfaction_feedback && (
                    <p className="text-sm text-amber-700 mt-1">{selectedBug.satisfaction_feedback}</p>
                  )}
                </div>
              )}
              
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium text-gray-700">Actions</p>
                
                {selectedBug.status !== 'resolved' && (
                  <div className="flex gap-2">
                    {selectedBug.status === 'new' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleUpdateStatus(selectedBug.id, 'in_progress')}
                      >
                        <Clock className="h-4 w-4 mr-1" /> Start Working
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setShowResolveDialog(selectedBug);
                        setResolutionMessage('');
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Resolve & Notify
                    </Button>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open(`mailto:${selectedBug.reporter_email}?subject=Re: Bug Report - ${selectedBug.title}`, '_blank')}
                  >
                    <Mail className="h-4 w-4 mr-1" /> Email Reporter
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(selectedBug.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Select a bug report to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Resolve Dialog */}
      <Dialog open={!!showResolveDialog} onOpenChange={() => setShowResolveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Resolve Bug & Notify Reporter
            </DialogTitle>
          </DialogHeader>
          {showResolveDialog && (
            <div className="space-y-4 pt-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-sm">{showResolveDialog.title}</p>
                <p className="text-sm text-gray-500">{showResolveDialog.reporter_email}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resolution Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={resolutionMessage}
                  onChange={(e) => setResolutionMessage(e.target.value)}
                  placeholder="Describe how the issue was resolved..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#003456]"
                />
              </div>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeSurvey}
                  onChange={(e) => setIncludeSurvey(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">Include satisfaction survey link</span>
              </label>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowResolveDialog(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleResolve}
                  disabled={resolving}
                >
                  {resolving ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" /> Resolve & Send Email</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BugReportsManager;
